import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { buildings } from '../services/api';

function BuildingModal({ building, onClose, onSave }) {
  const [form, setForm] = useState(building || {
    name: '', address: '', city: 'Vancouver', province: 'BC',
    postal_code: '', strata_plan: '', unit_count: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (building?.id) {
        await buildings.update(building.id, form);
        toast.success('Building updated');
      } else {
        await buildings.create(form);
        toast.success('Building added');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">{building?.id ? 'Edit' : 'Add'} Building</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">Building Name *</label>
            <input className="input" value={form.name} onChange={set('name')} required
              placeholder="e.g. Maple Ridge Strata" />
          </div>
          <div>
            <label className="label">Street Address *</label>
            <input className="input" value={form.address} onChange={set('address')} required
              placeholder="1234 Main St" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">City</label>
              <input className="input" value={form.city} onChange={set('city')} />
            </div>
            <div>
              <label className="label">Postal Code</label>
              <input className="input" value={form.postal_code} onChange={set('postal_code')} placeholder="V6B 1A1" />
            </div>
            <div>
              <label className="label">Strata Plan #</label>
              <input className="input" value={form.strata_plan} onChange={set('strata_plan')} placeholder="BCS1234" />
            </div>
            <div>
              <label className="label">Number of Units</label>
              <input type="number" className="input" value={form.unit_count} onChange={set('unit_count')}
                placeholder="24" min="1" />
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input resize-none" rows={3} value={form.notes} onChange={set('notes')}
              placeholder="Any important notes about this building…" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Building'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Buildings() {
  const [list, setList] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => {
    buildings.list()
      .then((r) => setList(r.data))
      .catch(() => toast.error('Failed to load buildings'));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this building? This cannot be undone.')) return;
    await buildings.delete(id);
    toast.success('Building deleted');
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Buildings</h1>
          <p className="text-gray-500 text-sm mt-1">{list.length} building{list.length !== 1 ? 's' : ''} in your portfolio</p>
        </div>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Building
        </button>
      </div>

      {list.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-gray-500 font-medium">No buildings yet</p>
          <button onClick={() => setModal('new')} className="mt-2 text-sm text-blue-600 hover:underline">
            Add your first building →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((b) => (
            <div key={b.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                  </svg>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setModal(b)} className="text-xs text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => handleDelete(b.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                </div>
              </div>
              <h3 className="font-semibold text-gray-900 mt-3">{b.name}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{b.address}</p>
              <p className="text-sm text-gray-400">{b.city}, {b.province} {b.postal_code}</p>
              <div className="flex gap-3 mt-3 pt-3 border-t text-xs text-gray-400">
                {b.strata_plan && <span>Plan: {b.strata_plan}</span>}
                {b.unit_count && <span>{b.unit_count} units</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <BuildingModal
          building={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}

      <style>{`
        .label { display: block; font-size: 0.75rem; font-weight: 500; color: #374151; margin-bottom: 0.25rem; }
        .input { width: 100%; border: 1px solid #d1d5db; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; }
        .input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
      `}</style>
    </div>
  );
}
